import { useEffect, type ReactNode } from 'react';
import { useAuthStore } from './token-store';
import { authService } from './authService';
import { refreshSingleFlight, onRefreshFailed } from './refreshClient';
import { usePagePermissionsStore } from './page-permissions';

// Refresh the access token ~2 min before it expires (port of main.kt:104-146).
const REFRESH_LEAD_MS = 2 * 60 * 1000;

/**
 * Thin provider that (a) runs the SSO callback parse once on mount, (b) flips `status`
 * from 'loading' -> resolved so <RequireAuth> doesn't flash the login page during hydration,
 * (c) schedules a proactive pre-expiry token refresh (doc 03 §4.5), and (d) loads
 * effective page permissions from the API (System & Permissions matrix).
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  useEffect(() => {
    // handleCallback is async (it may exchange a `?code=` for the token bundle) — resolve the auth
    // status only after it settles so a fresh login isn't briefly seen as unauthenticated.
    void authService.handleCallback().finally(() => {
      useAuthStore.getState().resolveStatus();
      const roles = useAuthStore.getState().session?.roles ?? [];
      void usePagePermissionsStore.getState().load(roles);
    });
  }, []);

  useEffect(() => {
    const unsubscribe = useAuthStore.subscribe((state, prev) => {
      if (!state.session) {
        usePagePermissionsStore.getState().clear();
        return;
      }
      const rolesChanged =
        prev.session?.roles !== state.session.roles ||
        prev.session?.accessToken !== state.session.accessToken;
      if (rolesChanged && state.status === 'authenticated') {
        void usePagePermissionsStore.getState().load(state.session.roles);
      }
    });
    return unsubscribe;
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    let scheduledFor: number | null = null;

    const schedule = (expiresAt: number | null) => {
      if (expiresAt === scheduledFor) return; // expiry unchanged -> keep the running timer
      scheduledFor = expiresAt;
      if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }
      if (!expiresAt) return;
      const delay = Math.max(0, expiresAt - Date.now() - REFRESH_LEAD_MS);
      timer = setTimeout(() => {
        void refreshSingleFlight().catch(() => {
          onRefreshFailed();
        });
      }, delay);
    };

    schedule(useAuthStore.getState().session?.accessTokenExpiresAt ?? null);
    const unsubscribe = useAuthStore.subscribe((state) => {
      schedule(state.session?.accessTokenExpiresAt ?? null);
    });
    return () => {
      if (timer) clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  return <>{children}</>;
}
